import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Alert, Modal, StyleSheet, Text, View } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { LocaleCode } from './locale';
import {
  getLocale,
  getLocaleDisplayName,
  setLocale as setLocaleCore,
  t as tCore,
  canRestartApp,
  tryRestartApp,
} from './locale';
import { isWebRuntime } from '../platform/runtimeTarget';

export const LOCALE_STORAGE_KEY = 'app_locale';

type I18nContextValue = {
  locale: LocaleCode;
  setLocale: (locale: LocaleCode) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  getLocaleDisplayName: (locale: LocaleCode) => string;
  /** True while a language change is applying (blocking overlay before restart). */
  isApplyingLocale: boolean;
};

const I18nContext = createContext<I18nContextValue | null>(null);

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    paddingVertical: 24,
    paddingHorizontal: 28,
    alignItems: 'center',
    minWidth: 220,
  },
  message: {
    marginTop: 14,
    fontSize: 15,
    textAlign: 'center',
    color: '#111827',
  },
});

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<LocaleCode>(() => getLocale());
  const [isApplyingLocale, setIsApplyingLocale] = useState(false);
  /** Locale we are switching away from — used to revert on cancel / keep-current. */
  const previous_locale_ref = useRef<LocaleCode>(getLocale());
  const pending_locale_ref = useRef<LocaleCode | null>(null);
  const apply_ref = useRef<(newLocale: LocaleCode) => void>(() => {});

  /**
   * Keep React `locale` aligned with the module locale.
   * App boot may call `setLocale` from AsyncStorage after this provider first rendered
   * (or remount timing can differ on web) — without this, the language toggle still
   * thinks locale is `en` while strings/RTL are already `ar`, so "switch to English" never fires.
   */
  useEffect(() => {
    let cancelled = false;
    const sync_from_module = () => {
      if (cancelled) {
        return;
      }
      const current = getLocale();
      setLocaleState(prev => (prev === current ? prev : current));
    };
    sync_from_module();
    void (async () => {
      try {
        const saved = await AsyncStorage.getItem(LOCALE_STORAGE_KEY);
        if (cancelled) {
          return;
        }
        const next = saved === 'ar' || saved === 'en' ? saved : getLocale();
        if (next !== getLocale()) {
          setLocaleCore(next);
        }
        setLocaleState(prev => (prev === next ? prev : next));
      } catch {
        sync_from_module();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const revertPendingLocale = useCallback(() => {
    const previous = previous_locale_ref.current;
    setLocaleCore(previous);
    setLocaleState(previous);
    AsyncStorage.setItem(LOCALE_STORAGE_KEY, previous).catch(() => {});
    pending_locale_ref.current = null;
    setIsApplyingLocale(false);
  }, []);

  const showRestartFailedChoices = useCallback(
    (newLocale: LocaleCode) => {
      Alert.alert(tCore('settings.restartFailedTitle'), tCore('settings.restartFailedMessage'), [
        {
          text: tCore('settings.restartTryAgain'),
          onPress: () => apply_ref.current(newLocale),
        },
        {
          text: tCore('settings.restartKeepCurrent'),
          style: 'cancel',
          onPress: () => {
            revertPendingLocale();
          },
        },
        {
          text: tCore('settings.restartReopenManually'),
          onPress: () => {
            // Persist only — keep this session on the current language/layout.
            // Cold start will apply locale + forceRTL (+ restart if the flag flips).
            AsyncStorage.setItem(LOCALE_STORAGE_KEY, newLocale).catch(() => {});
            revertPendingLocale();
            Alert.alert(
              tCore('settings.restartRequiredTitle'),
              tCore('settings.languageChangeMessage'),
              [{ text: tCore('common.ok') }],
            );
          },
        },
      ]);
    },
    [revertPendingLocale],
  );

  const applyLocaleAndRestart = useCallback(
    (newLocale: LocaleCode) => {
      previous_locale_ref.current = getLocale();
      pending_locale_ref.current = newLocale;
      setIsApplyingLocale(true);

      if (!canRestartApp()) {
        setIsApplyingLocale(false);
        pending_locale_ref.current = null;
        showRestartFailedChoices(newLocale);
        return;
      }

      setLocaleCore(newLocale);
      setLocaleState(newLocale);
      AsyncStorage.setItem(LOCALE_STORAGE_KEY, newLocale).catch(() => {});
      const scheduled = tryRestartApp();
      if (!scheduled) {
        revertPendingLocale();
        showRestartFailedChoices(newLocale);
        return;
      }
      // Web: location.reload is scheduled — do not arm the native "restart failed" timeout
      // (it would flash a false failure if unload is slightly delayed).
      if (isWebRuntime()) {
        return;
      }
      // If native restart never fires, surface failure choices instead of a stuck overlay.
      setTimeout(() => {
        if (pending_locale_ref.current !== newLocale) {
          return;
        }
        revertPendingLocale();
        showRestartFailedChoices(newLocale);
      }, 5000);
    },
    [revertPendingLocale, showRestartFailedChoices],
  );

  apply_ref.current = applyLocaleAndRestart;

  const setLocale = useCallback(
    (newLocale: LocaleCode) => {
      if (newLocale === getLocale()) {
        return;
      }
      // Restart immediately; failure alert only if automatic restart does not succeed.
      applyLocaleAndRestart(newLocale);
    },
    [applyLocaleAndRestart],
  );

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => tCore(key, params),
    [locale],
  );

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t,
      getLocaleDisplayName,
      isApplyingLocale,
    }),
    [locale, setLocale, t, isApplyingLocale],
  );

  return (
    <I18nContext.Provider value={value}>
      {children}
      <Modal visible={isApplyingLocale} transparent animationType="fade" statusBarTranslucent>
        <View style={styles.overlay} accessibilityViewIsModal>
          <View style={styles.card}>
            <ActivityIndicator size="large" />
            <Text style={styles.message}>{tCore('settings.restartRequiredMessage')}</Text>
          </View>
        </View>
      </Modal>
    </I18nContext.Provider>
  );
}

export function useTranslation(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    if (__DEV__) {
      console.warn(
        '[i18n] useTranslation() called outside I18nProvider — using module locale fallback.',
      );
    }
    return {
      locale: getLocale(),
      setLocale: () => {},
      t: (key: string, params?: Record<string, string | number>) => tCore(key, params),
      getLocaleDisplayName,
      isApplyingLocale: false,
    };
  }
  return ctx;
}
